/**
 * Shared Delivery Date Validation Utility
 * 
 * Validates potential delivery date against business rules:
 * - Cannot be before glass arrival date
 * - Cannot be before manufacture date
 * - Can only equal manufacture date if production is complete
 */

export interface DeliveryDateValidationParams {
  potentialDeliveryDate: Date | string;
  manufactureDate?: Date | string | null;
  glassArrivalDate?: Date | string | null;
  jobStatus?: string;
  productionStatus?: string | null;
}

/**
 * Validates potential delivery date against business rules
 * @param params Validation parameters
 * @returns Error message string if validation fails, null if valid
 */
export function validateDeliveryDate(params: DeliveryDateValidationParams): string | null {
  const {
    potentialDeliveryDate,
    manufactureDate,
    glassArrivalDate,
    jobStatus,
    productionStatus
  } = params;

  console.log('🔍 validateDeliveryDate called with:', {
    potentialDeliveryDate,
    manufactureDate,
    glassArrivalDate,
    jobStatus,
    productionStatus
  });

  if (!potentialDeliveryDate) {
    console.log('❌ No potential delivery date provided');
    return null; // No date to validate
  }

  const deliveryDate = new Date(potentialDeliveryDate);
  console.log('📅 Parsed delivery date:', deliveryDate.toISOString());

  // Validate against glass arrival date
  if (glassArrivalDate) {
    const glassDate = new Date(glassArrivalDate);
    if (deliveryDate < glassDate) {
      return 'Potential Delivery date cannot be before Glass Arrival date';
    }
  }

  // Validate against manufacture date
  if (manufactureDate) {
    const manufDate = new Date(manufactureDate);
    console.log('🏭 Manufacture date:', manufDate.toISOString());
    
    // Check if production is complete - if yes, allow any delivery date
    const isProductionComplete = jobStatus === 'completed' && productionStatus === 'production_complete';
    console.log('✅ Production complete check:', {
      jobStatus,
      productionStatus,
      isProductionComplete
    });
    
    if (isProductionComplete) {
      console.log('✅ Production is complete - allowing any delivery date');
      return null; // Production complete - no restrictions on delivery date
    }
    
    // Production NOT complete - apply restrictions
    console.log('⚠️ Production not complete - applying date restrictions');
    
    // Check if delivery date is before manufacture date
    if (deliveryDate < manufDate) {
      console.log('❌ Delivery before manufacture');
      return 'Potential Delivery date cannot be before Manufacturing date';
    }

    // Check if delivery date equals manufacture date (same day)
    const isSameDay = deliveryDate.toDateString() === manufDate.toDateString();
    console.log('📆 Same day check:', {
      deliveryDateString: deliveryDate.toDateString(),
      manufDateString: manufDate.toDateString(),
      isSameDay
    });
    
    if (isSameDay) {
      console.log('❌ Production not complete - blocking same day');
      return 'Delivery date can only be same as Manufacturing date if production is complete';
    }
  }

  console.log('✅ Validation passed');
  return null; // All validations passed
}

/**
 * Helper function to check if a date change is valid
 * Returns true if valid, false if invalid (with error logged)
 */
export function isDeliveryDateValid(params: DeliveryDateValidationParams): boolean {
  const error = validateDeliveryDate(params);
  return error === null;
}
